import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { AuthProvider } from './auth/AuthContext';
import { useAuth } from './auth/useAuth';
import { AppShell } from './components/AppShell';
import { CompetitorsPage } from './pages/CompetitorsPage';
import { CrawlerPage } from './pages/CrawlerPage';
import { DashboardPage } from './pages/DashboardPage';
import { InsightsPage } from './pages/InsightsPage';
import { LoginPage } from './pages/LoginPage';
import { LogsPage } from './pages/LogsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { ProductsPage } from './pages/ProductsPage';
import { SettingsPage } from './pages/SettingsPage';
import { RecommendationsPage } from './pages/RecommendationsPage';
import { ZenRowsPage } from './pages/ZenRowsPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Nothing but the sign-in screen exists until there is a session.
 *
 * The check lives above the router rather than in a per-route wrapper, so a page
 * added later cannot be reachable by having forgotten to wrap it.
 */
function AuthenticatedApp() {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <Router>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/recommendations" element={<RecommendationsPage />} />
          <Route path="/competitors" element={<CompetitorsPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/crawler" element={<CrawlerPage />} />
          <Route path="/logs" element={<LogsPage />} />
          <Route path="/zenrows" element={<ZenRowsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Router>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthenticatedApp />
      </AuthProvider>
    </QueryClientProvider>
  );
}
